import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Chip, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Lightbulb, 
  TrendingUp, 
  QuestionAnswer, 
  AutoFixHigh,
  Business,
  Psychology
} from '@mui/icons-material';

interface InsightsPanelProps {
  insights?: string;
  suggestions?: string[];
  followUpQuestions?: string[];
  selfHealingOptions?: string[];
  businessContext?: string;
  userRole?: string;
  examples?: string[];
  onFollowUpClick?: (question: string) => void;
  onSelfHealingClick?: (option: string) => void;
}

export function InsightsPanel({
  insights,
  suggestions,
  followUpQuestions,
  selfHealingOptions,
  businessContext,
  userRole,
  examples,
  onFollowUpClick,
  onSelfHealingClick
}: InsightsPanelProps) {
  if (!insights && !suggestions && !followUpQuestions && !selfHealingOptions && !businessContext && !examples) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        {/* Business Context */}
        {businessContext && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Business sx={{ mr: 1, fontSize: '1rem', color: 'primary.main' }} />
              Business Context
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {businessContext}
            </Typography>
          </Box>
        )}

        {/* User Role */}
        {userRole && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Psychology sx={{ mr: 1, fontSize: '1rem', color: 'secondary.main' }} />
              Detected Role: {userRole}
            </Typography>
          </Box>
        )}

        {/* Insights */}
        {insights && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Lightbulb sx={{ mr: 1, fontSize: '1rem', color: '#ffc107' }} />
              Business Insights
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {insights}
            </Typography>
          </Box>
        )}

        {/* Suggestions */}
        {suggestions && suggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUp sx={{ mr: 1, fontSize: '1rem', color: 'success.main' }} />
              Suggestions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {suggestions.map((suggestion, index) => (
                <Chip 
                  key={index} 
                  label={suggestion} 
                  size="small" 
                  variant="outlined"
                  color="success"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Follow-up Questions */}
        {followUpQuestions && followUpQuestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <QuestionAnswer sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
              Follow-up Questions
            </Typography>
            <List dense>
              {followUpQuestions.map((question, index) => (
                <ListItem 
                  key={index} 
                  sx={{ 
                    py: 0.5,
                    cursor: onFollowUpClick ? 'pointer' : 'default',
                    '&:hover': onFollowUpClick ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {}
                  }}
                  onClick={onFollowUpClick ? () => onFollowUpClick(question) : undefined}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="caption" color="text.secondary">
                      {index + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary={question} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Self-Healing Options */}
        {selfHealingOptions && selfHealingOptions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AutoFixHigh sx={{ mr: 1, fontSize: '1rem', color: 'warning.main' }} />
              Self-Healing Options
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selfHealingOptions.map((option, index) => (
                <Chip 
                  key={index} 
                  label={option} 
                  size="small" 
                  variant="outlined"
                  color="warning"
                  clickable
                  onClick={onSelfHealingClick ? () => onSelfHealingClick(option) : undefined}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Examples */}
        {examples && examples.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Lightbulb sx={{ mr: 1, fontSize: '1rem', color: 'primary.main' }} />
              Examples
            </Typography>
            <List dense>
              {examples.map((example, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="caption" color="text.secondary">
                      {index + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary={example} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
